import React, { createContext, useContext, useMemo } from 'react';
import type {
	IssueNavigatorFeaturesContext,
	IssueNavigatorFeatureProviderProps,
	IssueNavigatorFeatureGuardProps,
} from './types.tsx';

const defaultFeatureContext: IssueNavigatorFeaturesContext = {
	isViewSwitcherEnabled: true,
	isOnboardingEnabled: true,
	isExportMenuEnabled: true,
	isRowContextMenuEnabled: true,
	isSaveFilterButtonEnabled: true,
	isBulkChangeWorkItemsEnabled: true,
	isImportWorkItemsFromCSVEnabled: true,
	isViewAsChartEnabled: true,
	isConditionalFormattingEnabled: false, // TODO: jira_list_conditional_formatting
	isConditionalFormattingEditingEnabled: false, // TODO: set this feature to true when cleaning up saved_list_view_technical_changes
	isProjectQuickstartOnboardingEnabled: false,
};

const IssueNavigatorFeatureContext =
	createContext<IssueNavigatorFeaturesContext>(defaultFeatureContext);

const useIssueNavigatorFeatures = () => useContext(IssueNavigatorFeatureContext);

const useIssueNavigatorFeature = (featureKey: keyof IssueNavigatorFeaturesContext) =>
	useIssueNavigatorFeatures()[featureKey];

const IssueNavigatorFeatureProvider = ({
	children,
	isBulkChangeWorkItemsEnabled,
	isConditionalFormattingEnabled,
	isConditionalFormattingEditingEnabled,
	isExportMenuEnabled,
	isImportWorkItemsFromCSVEnabled,
	isOnboardingEnabled,
	isProjectQuickstartOnboardingEnabled,
	isRowContextMenuEnabled,
	isSaveFilterButtonEnabled,
	isViewAsChartEnabled,
	isViewSwitcherEnabled,
}: IssueNavigatorFeatureProviderProps) => {
	const value = useMemo(
		() => ({
			isBulkChangeWorkItemsEnabled:
				isBulkChangeWorkItemsEnabled ?? defaultFeatureContext.isBulkChangeWorkItemsEnabled,
			isConditionalFormattingEnabled:
				isConditionalFormattingEnabled ?? defaultFeatureContext.isConditionalFormattingEnabled,
			isConditionalFormattingEditingEnabled:
				isConditionalFormattingEditingEnabled ??
				defaultFeatureContext.isConditionalFormattingEditingEnabled,
			isExportMenuEnabled: isExportMenuEnabled ?? defaultFeatureContext.isExportMenuEnabled,
			isImportWorkItemsFromCSVEnabled:
				isImportWorkItemsFromCSVEnabled ?? defaultFeatureContext.isImportWorkItemsFromCSVEnabled,
			isOnboardingEnabled: isOnboardingEnabled ?? defaultFeatureContext.isOnboardingEnabled,
			isProjectQuickstartOnboardingEnabled:
				isProjectQuickstartOnboardingEnabled ??
				defaultFeatureContext.isProjectQuickstartOnboardingEnabled,
			isRowContextMenuEnabled:
				isRowContextMenuEnabled ?? defaultFeatureContext.isRowContextMenuEnabled,
			isSaveFilterButtonEnabled:
				isSaveFilterButtonEnabled ?? defaultFeatureContext.isSaveFilterButtonEnabled,
			isViewAsChartEnabled: isViewAsChartEnabled ?? defaultFeatureContext.isViewAsChartEnabled,
			isViewSwitcherEnabled: isViewSwitcherEnabled ?? defaultFeatureContext.isViewSwitcherEnabled,
		}),
		[
			isBulkChangeWorkItemsEnabled,
			isConditionalFormattingEnabled,
			isConditionalFormattingEditingEnabled,
			isExportMenuEnabled,
			isImportWorkItemsFromCSVEnabled,
			isOnboardingEnabled,
			isProjectQuickstartOnboardingEnabled,
			isRowContextMenuEnabled,
			isSaveFilterButtonEnabled,
			isViewAsChartEnabled,
			isViewSwitcherEnabled,
		],
	);

	return (
		<IssueNavigatorFeatureContext.Provider value={value}>
			{children}
		</IssueNavigatorFeatureContext.Provider>
	);
};

const IssueNavigatorFeatureGuard = ({ featureKey, on, off }: IssueNavigatorFeatureGuardProps) => {
	const feature = useIssueNavigatorFeature(featureKey);
	return feature ? on : off;
};

export {
	IssueNavigatorFeatureProvider,
	useIssueNavigatorFeatures,
	useIssueNavigatorFeature,
	IssueNavigatorFeatureGuard,
};
